/* ============================================================
   Strowes Kicks — theme.js
   Vanilla ES6+ | IIFE | No external dependencies
   ============================================================ */
(function () {
  'use strict';

  /* ── Utilities ──────────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const off = (el, ev, fn) => el && el.removeEventListener(ev, fn);

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ── Toast Notification ─────────────────────────────────── */
  const Toast = {
    el: null,
    timer: null,
    init() { this.el = $('#toast'); },
    show(msg, type = 'success') {
      if (!this.el) return;
      clearTimeout(this.timer);
      this.el.textContent = msg;
      this.el.className = `toast is-visible toast--${type}`;
      this.timer = setTimeout(() => {
        this.el.classList.remove('is-visible');
      }, 2500);
    }
  };

  /* ── Body Scroll Lock ───────────────────────────────────── */
  let scrollY = 0;
  function lockScroll() {
    scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
  }
  function unlockScroll() {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  /* ── Keyboard Trap ──────────────────────────────────────── */
  function trapFocus(el) {
    const focusable = $$(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      el
    ).filter(e => !e.closest('[hidden]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function handleTab(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    el._trapHandler = handleTab;
    on(el, 'keydown', handleTab);
    first.focus();
  }
  function releaseFocus(el) {
    if (el && el._trapHandler) off(el, 'keydown', el._trapHandler);
  }

  /* ── Cart State ─────────────────────────────────────────── */
  const Cart = {
    data: null,

    async fetch() {
      const res = await fetch('/cart.js');
      this.data = await res.json();
      return this.data;
    },

    async add(variantId, qty = 1, properties = {}) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty, properties })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add item');
      }
      await this.fetch();
      this.updateBadge();
      return this.data;
    },

    async change(key, qty) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      this.data = await res.json();
      this.updateBadge();
      return this.data;
    },

    updateBadge() {
      const count = this.data ? this.data.item_count : 0;
      $$('.cart-badge').forEach(el => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        el.classList.add('bump');
        setTimeout(() => el.classList.remove('bump'), 300);
      });
    }
  };

  /* ── Cart Drawer ────────────────────────────────────────── */
  const CartDrawer = {
    drawer: null,
    backdrop: null,
    body: null,
    foot: null,
    isOpen: false,

    init() {
      this.drawer = $('#cart-drawer');
      this.backdrop = $('#cart-overlay');
      this.body = $('#cart-drawer-body');
      this.foot = $('#cart-drawer-foot');

      on($('#cart-drawer-close'), 'click', () => this.close());
      on(this.backdrop, 'click', () => this.close());
      on(document, 'keydown', e => { if (e.key === 'Escape' && this.isOpen) this.close(); });
    },

    async open() {
      await this.render();
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.backdrop.classList.add('is-visible');
      lockScroll();
      trapFocus(this.drawer);
      this.isOpen = true;
    },

    close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.backdrop.classList.remove('is-visible');
      releaseFocus(this.drawer);
      unlockScroll();
      this.isOpen = false;
    },

    async render() {
      await Cart.fetch();
      const cart = Cart.data;

      if (!cart || cart.item_count === 0) {
        this.body.innerHTML = `
          <div class="cart-empty">
            <div class="cart-empty__icon">👟</div>
            <h3 class="cart-empty__title">Your Cart Is Empty</h3>
            <p class="cart-empty__text">Time to find your next pair.</p>
            <a href="/collections/all" class="btn btn--primary">Shop Now</a>
          </div>`;
        this.foot.innerHTML = '';
        return;
      }

      this.body.innerHTML = cart.items.map(item => `
        <div class="cart-item" data-key="${item.key}">
          <img class="cart-item__img"
            src="${item.image ? item.image.replace('.jpg', '_160x160.jpg').replace('.png', '_160x160.png') : ''}"
            alt="${this._escape(item.product_title)}"
            width="88" height="88"
            loading="lazy">
          <div class="cart-item__info">
            <a class="cart-item__title" href="${item.url}">${this._escape(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title'
              ? `<span class="cart-item__variant">${this._escape(item.variant_title)}</span>` : ''}
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
            <div class="cart-item__controls">
              <div class="qty-ctrl">
                <button class="qty-ctrl__btn" data-action="dec" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                <input class="qty-ctrl__input" type="number" value="${item.quantity}" min="0" data-key="${item.key}" aria-label="Quantity">
                <button class="qty-ctrl__btn" data-action="inc" data-key="${item.key}" aria-label="Increase quantity">+</button>
              </div>
              <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove ${this._escape(item.product_title)}">Remove</button>
            </div>
          </div>
        </div>`).join('');

      this.foot.innerHTML = `
        <div class="cart-drawer__subtotal">
          <span>Subtotal</span>
          <span>${formatMoney(cart.total_price)}</span>
        </div>
        <p class="cart-drawer__tax">Taxes and shipping calculated at checkout</p>
        <a href="/checkout" class="cart-drawer__checkout">Checkout</a>
        <a href="/cart" class="cart-drawer__view">View Cart</a>`;

      this._bindItemEvents();
    },

    _bindItemEvents() {
      $$('.qty-ctrl__btn', this.body).forEach(btn => {
        on(btn, 'click', async () => {
          const key = btn.dataset.key;
          const item = Cart.data.items.find(i => i.key === key);
          if (!item) return;
          const newQty = btn.dataset.action === 'inc' ? item.quantity + 1 : item.quantity - 1;
          await Cart.change(key, newQty);
          await this.render();
        });
      });

      $$('.qty-ctrl__input', this.body).forEach(input => {
        on(input, 'change', async () => {
          const key = input.dataset.key;
          const qty = parseInt(input.value, 10);
          if (!isNaN(qty) && qty >= 0) {
            await Cart.change(key, qty);
            await this.render();
          }
        });
      });

      $$('.cart-item__remove', this.body).forEach(btn => {
        on(btn, 'click', async () => {
          await Cart.change(btn.dataset.key, 0);
          await this.render();
        });
      });
    },

    _escape(str) {
      return String(str).replace(/[&<>"']/g, m =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
    }
  };

  /* ── Search Overlay ─────────────────────────────────────── */
  const Search = {
    overlay: null,
    input: null,

    init() {
      this.overlay = $('#search-overlay');
      this.input = $('#search-overlay-input');
      on($('#search-close'), 'click', () => this.close());
      on(document, 'keydown', e => { if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close(); });
      on($('.search-overlay__input-wrap', this.overlay), 'click', e => e.stopPropagation());
      on(this.overlay, 'click', e => { if (e.target === this.overlay) this.close(); });
    },

    open() {
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      lockScroll();
      setTimeout(() => this.input && this.input.focus(), 100);
    },

    close() {
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }
  };

  /* ── Mobile Nav ─────────────────────────────────────────── */
  const MobileNav = {
    nav: null,
    backdrop: null,

    init() {
      this.nav = $('#mobile-nav');
      this.backdrop = $('#mobile-nav-overlay');
      on($('#mobile-nav-close'), 'click', () => this.close());
      on(this.backdrop, 'click', () => this.close());
      on(document, 'keydown', e => { if (e.key === 'Escape' && this.nav.classList.contains('is-open')) this.close(); });
    },

    open() {
      this.nav.classList.add('is-open');
      this.nav.setAttribute('aria-hidden', 'false');
      this.backdrop.classList.add('is-visible');
      lockScroll();
      trapFocus(this.nav);
    },

    close() {
      this.nav.classList.remove('is-open');
      this.nav.setAttribute('aria-hidden', 'true');
      this.backdrop.classList.remove('is-visible');
      releaseFocus(this.nav);
      unlockScroll();
    }
  };

  /* ── Header Scroll Behavior ─────────────────────────────── */
  function initHeaderScroll() {
    const header = $('.site-header');
    if (!header) return;
    let lastY = 0;

    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('scrolled', y > 80);
      lastY = y;
    }, { passive: true });
  }

  /* ── Animation Observer ─────────────────────────────────── */
  function initAnimations() {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      $$('.will-animate').forEach(el => { el.style.opacity = '1'; el.style.transform = 'none'; });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        el.classList.add('is-animated');
        // Line-expand variant
        if (el.classList.contains('will-animate--line')) {
          el.classList.add('is-animated--line');
        }
        observer.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });

    $$('.will-animate').forEach(el => observer.observe(el));

    // Statement banner clip reveal
    const clipObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-clipped');
          clipObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });

    $$('.statement-banner__text-inner').forEach(el => clipObserver.observe(el));

    // Line expand rules
    const lineObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'lineExpand 0.6s ease forwards';
          lineObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    $$('.hero-statement__rule, .brand-message__rule').forEach(el => lineObserver.observe(el));
  }

  /* ── Brand Message Letter Animation ─────────────────────── */
  function initLetterAnimation() {
    const textEls = $$('[data-letter-animate]');
    if (!textEls.length) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    textEls.forEach(el => {
      if (prefersReduced) return;
      const text = el.textContent;
      el.innerHTML = text.split('').map(char =>
        char === ' ' ? ' ' : `<span class="letter">${char}</span>`
      ).join('');

      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        const letters = $$('.letter', el);
        letters.forEach((span, i) => {
          setTimeout(() => { span.style.opacity = '1'; }, i * 15);
        });
        el.classList.add('letters-visible');
        observer.unobserve(el);
      }, { threshold: 0.4 });

      observer.observe(el);
    });
  }

  /* ── Product Card Quick Add ─────────────────────────────── */
  function initQuickAdd() {
    $$('.product-card').forEach(card => {
      const sizeBtns = $$('.size-btn', card);
      const addBtn = $('.quick-add-btn', card);
      let selectedVariantId = null;

      // Pre-select first available size
      const firstAvail = sizeBtns.find(b => !b.classList.contains('is-unavailable'));
      if (firstAvail) {
        firstAvail.classList.add('is-selected');
        selectedVariantId = firstAvail.dataset.variantId;
      }

      sizeBtns.forEach(btn => {
        on(btn, 'click', e => {
          e.preventDefault();
          e.stopPropagation();
          if (btn.classList.contains('is-unavailable')) return;
          sizeBtns.forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          selectedVariantId = btn.dataset.variantId;
        });
      });

      if (!addBtn) return;
      on(addBtn, 'click', async e => {
        e.preventDefault();
        e.stopPropagation();

        if (!selectedVariantId) {
          if (sizeBtns.length === 0) {
            selectedVariantId = addBtn.dataset.variantId;
          } else {
            Toast.show('Please select a size', 'error');
            return;
          }
        }

        const originalText = addBtn.textContent;
        addBtn.textContent = 'Adding...';
        addBtn.disabled = true;
        addBtn.classList.add('is-loading');

        try {
          await Cart.add(selectedVariantId, 1);
          addBtn.textContent = 'Added ✓';
          addBtn.classList.remove('is-loading');
          Toast.show('Added to cart', 'success');
          setTimeout(() => {
            addBtn.textContent = originalText;
            addBtn.disabled = false;
          }, 1500);
        } catch (err) {
          addBtn.textContent = 'Sold Out';
          addBtn.classList.remove('is-loading');
          Toast.show(err.message || 'Could not add item', 'error');
          setTimeout(() => {
            addBtn.textContent = originalText;
            addBtn.disabled = false;
          }, 2000);
        }
      });
    });
  }

  /* ── Testimonials Carousel ──────────────────────────────── */
  function initTestimonialsCarousel() {
    const carousel = $('.testimonials-carousel');
    if (!carousel) return;

    const track = $('.testimonials-track', carousel);
    const cards = $$('.testimonial-card', track);
    const prevBtn = $('.testimonials-btn--prev', carousel);
    const nextBtn = $('.testimonials-btn--next', carousel);

    if (!track || cards.length < 2) return;

    let currentIndex = 0;
    let autoTimer = null;
    let isDragging = false;
    let dragStartX = 0;
    let dragStartTranslate = 0;

    function getPerPage() {
      if (window.innerWidth <= 749) return 1;
      if (window.innerWidth <= 1100) return 2;
      return 3;
    }

    function getTranslate() {
      const card = cards[0];
      const gap = 24; // matches CSS gap: 2.4rem
      return -(currentIndex * (card.offsetWidth + gap));
    }

    function slide(dir) {
      const perPage = getPerPage();
      const maxIndex = Math.max(0, cards.length - perPage);
      currentIndex = Math.max(0, Math.min(currentIndex + dir, maxIndex));
      track.style.transform = `translateX(${getTranslate()}px)`;
    }

    function startAuto() {
      stopAuto();
      autoTimer = setInterval(() => slide(1), 5000);
    }

    function stopAuto() { clearTimeout(autoTimer); }

    on(prevBtn, 'click', () => { slide(-1); startAuto(); });
    on(nextBtn, 'click', () => { slide(1); startAuto(); });

    // Swipe / drag
    on(track, 'pointerdown', e => {
      isDragging = true;
      dragStartX = e.clientX;
      dragStartTranslate = getTranslate();
      track.style.transition = 'none';
      track.setPointerCapture(e.pointerId);
    });
    on(track, 'pointermove', e => {
      if (!isDragging) return;
      const delta = e.clientX - dragStartX;
      track.style.transform = `translateX(${dragStartTranslate + delta}px)`;
    });
    on(track, 'pointerup', e => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = '';
      const delta = e.clientX - dragStartX;
      if (Math.abs(delta) > 60) slide(delta < 0 ? 1 : -1);
      else track.style.transform = `translateX(${getTranslate()}px)`;
      startAuto();
    });

    on(carousel, 'mouseenter', stopAuto);
    on(carousel, 'mouseleave', startAuto);
    on(window, 'resize', () => {
      currentIndex = 0;
      track.style.transform = `translateX(0)`;
    });

    startAuto();
  }

  /* ── Product Page Gallery ───────────────────────────────── */
  function initProductGallery() {
    const gallery = $('.product-gallery');
    if (!gallery) return;

    const mainImg = $('.gallery-main img', gallery);
    const thumbs = $$('.gallery-thumb', gallery);

    thumbs.forEach(thumb => {
      on(thumb, 'click', () => {
        if (mainImg) {
          mainImg.src = thumb.dataset.src;
          mainImg.alt = thumb.dataset.alt || '';
        }
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* ── Product Page Variant Selection ─────────────────────── */
  function initVariantSelector() {
    const form = $('.product-form');
    if (!form) return;

    const hiddenId = form.querySelector('[name="id"]');
    const sizeBtns = $$('.product-size-btn', form);
    const priceEl = $('.product-info__price', form.closest('.product-page__grid') || document);
    const compareEl = priceEl ? priceEl.nextElementSibling : null;

    sizeBtns.forEach(btn => {
      on(btn, 'click', () => {
        if (btn.classList.contains('is-unavailable')) return;
        sizeBtns.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        if (hiddenId) hiddenId.value = btn.dataset.variantId;

        // Update price
        if (priceEl && btn.dataset.price) {
          priceEl.textContent = formatMoney(parseInt(btn.dataset.price, 10));
          priceEl.classList.toggle('product-info__price--sale', !!btn.dataset.comparePrice);
        }
        if (compareEl && btn.dataset.comparePrice) {
          compareEl.textContent = formatMoney(parseInt(btn.dataset.comparePrice, 10));
          compareEl.style.display = 'inline';
        } else if (compareEl) {
          compareEl.style.display = 'none';
        }

        // Update submit
        const addBtn = $('.product-form__add', form);
        if (addBtn) {
          const avail = btn.dataset.available === 'true';
          addBtn.disabled = !avail;
          addBtn.textContent = avail ? 'Add to Cart' : 'Sold Out';
        }
      });
    });

    // Form submit
    on(form, 'submit', async e => {
      e.preventDefault();
      if (!hiddenId || !hiddenId.value) {
        Toast.show('Please select a size', 'error');
        return;
      }

      const addBtn = $('.product-form__add', form);
      const originalText = addBtn ? addBtn.textContent : 'Add to Cart';
      if (addBtn) { addBtn.textContent = 'Adding...'; addBtn.disabled = true; }

      try {
        await Cart.add(parseInt(hiddenId.value, 10), 1);
        if (addBtn) { addBtn.textContent = 'Added ✓'; }
        Toast.show('Added to cart', 'success');
        setTimeout(async () => {
          if (addBtn) { addBtn.textContent = originalText; addBtn.disabled = false; }
          await CartDrawer.open();
        }, 600);
      } catch (err) {
        if (addBtn) { addBtn.textContent = 'Error — Try Again'; addBtn.disabled = false; }
        Toast.show(err.message || 'Could not add item', 'error');
      }
    });
  }

  /* ── Accordion ──────────────────────────────────────────── */
  function initAccordions() {
    $$('.accordion-trigger').forEach(trigger => {
      on(trigger, 'click', () => {
        const item = trigger.closest('.accordion-item');
        const content = item.querySelector('.accordion-content');
        const inner = content.querySelector('.accordion-content__inner');
        const isOpen = item.classList.contains('is-open');

        // Close all others in same container
        const siblings = $$('.accordion-item.is-open', item.parentElement);
        siblings.forEach(sib => {
          sib.classList.remove('is-open');
          const sibContent = sib.querySelector('.accordion-content');
          if (sibContent) sibContent.style.height = '0';
          const sibTrigger = sib.querySelector('.accordion-trigger');
          if (sibTrigger) sibTrigger.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          item.classList.add('is-open');
          content.style.height = inner.offsetHeight + 'px';
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ── Newsletter Form ─────────────────────────────────────── */
  function initNewsletter() {
    $$('.newsletter-section-form').forEach(form => {
      // The form is handled by Shopify's native customer form
      // We just add a success class after redirect
      const msg = form.querySelector('.newsletter__success');
      if (msg && new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
        msg.classList.add('is-visible');
        form.querySelector('.newsletter__form')?.style?.setProperty('display', 'none');
      }
    });
  }

  /* ── Stagger Cards on Scroll ─────────────────────────────── */
  function initCardStagger() {
    const grids = $$('.product-grid, .blog-grid');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const cards = $$('.product-card, .article-card', entry.target);
        cards.forEach((card, i) => {
          card.style.transitionDelay = `${i * 0.07}s`;
          card.classList.add('is-animated');
        });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1 });

    grids.forEach(g => {
      $$('.product-card, .article-card', g).forEach(c => {
        c.classList.add('will-animate', 'will-animate--up');
      });
      observer.observe(g);
    });
  }

  /* ── Sort Select (Collection) ────────────────────────────── */
  function initSortSelect() {
    const sel = $('.sort-select');
    if (!sel) return;
    on(sel, 'change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      window.location.href = url.toString();
    });
  }

  /* ── Cart Page Update ────────────────────────────────────── */
  function initCartPage() {
    const form = $('.cart-form');
    if (!form) return;
    // Update button handled natively by Shopify form submit
  }

  /* ── Global Event Delegation ─────────────────────────────── */
  function bindGlobalEvents() {
    on(document, 'click', e => {
      // Open cart drawer
      if (e.target.closest('[data-action="open-cart"]')) {
        e.preventDefault();
        CartDrawer.open();
        return;
      }
      // Open search
      if (e.target.closest('[data-action="open-search"]')) {
        e.preventDefault();
        Search.open();
        return;
      }
      // Open mobile nav
      if (e.target.closest('[data-action="open-nav"]')) {
        e.preventDefault();
        MobileNav.open();
        return;
      }
    });
  }

  /* ── Init ────────────────────────────────────────────────── */
  function init() {
    Toast.init();
    CartDrawer.init();
    Search.init();
    MobileNav.init();

    Cart.fetch().then(() => Cart.updateBadge());

    bindGlobalEvents();
    initHeaderScroll();
    initAnimations();
    initLetterAnimation();
    initQuickAdd();
    initTestimonialsCarousel();
    initProductGallery();
    initVariantSelector();
    initAccordions();
    initNewsletter();
    initCardStagger();
    initSortSelect();
    initCartPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
